// ============================================================
// DEMO DATA — Firestore-shaped flat docs, ported verbatim from
// mock/index.html. This is what mockAdapter serves today; firebaseAdapter
// will replace it with live Firestore reads (same shapes).
// All data is fictitious (no GUGA / real client data — PRD §10 隔離原則).
// ============================================================
import type {
  AxKpi,
  AxMemberProgress,
  AxPhase,
  AxSupport,
  AxWin,
  AxCase,
  CalendarEvent,
  DriveFile,
  Knowledge,
  Member,
  MeetingNote,
  MemberApp,
  OneOnOne,
  Project,
  Prompt,
} from '../types/models';

// ── PROJECTS — PJ進捗・KGI/KPIの正本。pmId は PRD §4-1 の各PJ責任者。 ──
export const PROJECTS: Project[] = [
  {
    id: 'xfactory',
    name: 'xFactory',
    color: '#F59E0B',
    pmId: 'taiki',
    progress: 55,
    health: 'watch',
    nextAction: '制作アプリ改修を完了し、アウトリーチ30件を追加送付',
    kgi: '月10件の新規受注（HP制作）を安定して獲得する',
    kgiProgress: 42,
    kpis: [
      { label: 'アウトバウンド返信率', value: 0.28, unit: '%', target: '目標 0.3%', trend: 'up', goodWhen: 'high' },
      { label: '商談化', value: 6, unit: '件/月', target: '目標 10件', trend: 'up', goodWhen: 'high' },
      { label: '受注', value: 3, unit: '件/月', target: '目標 10件', trend: 'flat', goodWhen: 'high' },
      { label: '送付リスト', value: 120, unit: '件', target: '目標 150件', trend: 'up', goodWhen: 'high' },
    ],
    milestones: [
      { label: 'PoC実証（多言語マニュアル動画）', done: true },
      { label: '制作アプリ（Web制作）改修', done: false },
      { label: 'アウトバウンド返信率 0.3% 達成', done: false },
      { label: '就労支援ワーカーのオペ文書化', done: false },
    ],
  },
  {
    id: 'casa66',
    name: 'casa.66',
    color: '#10B981',
    pmId: 'doi',
    progress: 72,
    health: 'good',
    nextAction: '新商品の撮影実施・ECページ反映',
    kgi: 'ECページの拡充と出荷オペレーションの安定化',
    kgiProgress: 66,
    kpis: [
      { label: '撮影完了', value: 8, unit: '商品', target: '目標 12商品', trend: 'up', goodWhen: 'high' },
      { label: '出荷リードタイム', value: 2.1, unit: '日', target: '目標 1.5日', trend: 'down', goodWhen: 'low' },
      { label: '欠品リスクSKU', value: 2, unit: '件', target: '目標 0件', trend: 'down', goodWhen: 'low' },
    ],
    milestones: [
      { label: '新商品ラインの撮影スケジュール確定', done: true },
      { label: '欠品リスクSKUの追加発注', done: true },
      { label: '撮影・EC反映', done: false },
      { label: '梱包工程の見直し', done: false },
    ],
  },
  {
    id: 'yakuin',
    name: '役員会議',
    color: '#475569',
    pmId: 'taiki',
    progress: 60,
    health: 'good',
    nextAction: '下期KPI報告フォーマットの整備',
    kgi: '各PJのKPIを可視化し、下期の投資判断を回す体制を確立する',
    kgiProgress: 55,
    kpis: [
      { label: 'KPI報告できるPJ', value: 5, unit: '/7 PJ', target: '目標 7PJ', trend: 'up', goodWhen: 'high' },
      { label: '意思決定リードタイム', value: 4, unit: '日', target: '目標 2日', trend: 'down', goodWhen: 'low' },
      { label: '承認済み投資テーマ', value: 2, unit: '件', target: '目標 3件', trend: 'flat', goodWhen: 'high' },
    ],
    milestones: [
      { label: '上期進捗レビュー', done: true },
      { label: 'AIアバター発信スタジオ内部展開の承認', done: true },
      { label: '下期KPI報告体制の確立', done: false },
    ],
  },
  {
    id: 'yaoyorozu',
    name: 'ヤオヨロズ',
    color: '#6366F1',
    pmId: 'taichi',
    progress: 68,
    health: 'good',
    nextAction: '好調企画の横展開フォーマット運用',
    kgi: '好調企画の横展開を仕組み化し、月次の制作量と反応を伸ばす',
    kgiProgress: 62,
    kpis: [
      { label: '当月公開企画', value: 9, unit: '本', target: '目標 12本', trend: 'up', goodWhen: 'high' },
      { label: '平均エンゲージ率', value: 4.2, unit: '%', target: '目標 5%', trend: 'up', goodWhen: 'high' },
      { label: '横展開した企画', value: 3, unit: '本', target: '目標 5本', trend: 'flat', goodWhen: 'high' },
    ],
    milestones: [
      { label: '当月重点施策の決定', done: true },
      { label: '制作分担の再設定', done: true },
      { label: '横展開企画の定着', done: false },
    ],
  },
  {
    id: 'sns',
    name: 'SNS運用コンサル',
    color: '#0EA5E9',
    pmId: 'ren',
    progress: 64,
    health: 'good',
    nextAction: 'クライアント報告資料の週次定型化を完了',
    kgi: 'クライアントのアカウントを伸ばし、週次報告を定型運用で回す',
    kgiProgress: 58,
    kpis: [
      { label: '平均エンゲージ率', value: 5.1, unit: '%', target: '目標 5%', trend: 'up', goodWhen: 'high' },
      { label: '週次投稿', value: 12, unit: '本/週', target: '目標 15本', trend: 'up', goodWhen: 'high' },
      { label: '報告書 定型化率', value: 70, unit: '%', target: '目標 100%', trend: 'up', goodWhen: 'high' },
    ],
    milestones: [
      { label: '高エンゲージメントのフォーマット特定', done: true },
      { label: '来週投稿の方向性確定', done: true },
      { label: 'クライアント報告資料の定型化', done: false },
    ],
  },
  {
    id: 'ax',
    name: 'AXコンサル・IZANAI',
    color: '#B45309',
    pmId: 'kurita',
    progress: 30,
    health: 'watch',
    nextAction: '現状業務フローの整理と初期スコープ提案',
    kgi: '初期スコープ2業務で生成AI導入の効果を実証し、横展開につなげる',
    kgiProgress: 28,
    kpis: [
      { label: '対象業務スコープ', value: 1, unit: '/2 業務', target: '目標 2業務', trend: 'flat', goodWhen: 'high' },
      { label: 'ヒアリング完了', value: 60, unit: '%', target: '目標 100%', trend: 'up', goodWhen: 'high' },
      { label: '想定削減時間', value: 8, unit: 'h/週', target: '目標 10h', trend: 'up', goodWhen: 'high' },
    ],
    milestones: [
      { label: 'キックオフ・現状ヒアリング', done: true },
      { label: '初期スコープの確定（2業務）', done: false },
      { label: '効果検証フェーズ', done: false },
    ],
  },
  {
    id: 'jimukyoku',
    name: '事務局',
    color: '#6B7280',
    pmId: 'hayato',
    progress: 58,
    health: 'good',
    nextAction: '問い合わせFAQの拡充を完了',
    kgi: '会員対応・発送・問い合わせを誰でも回せる標準オペにする',
    kgiProgress: 52,
    kpis: [
      { label: 'FAQカバー率', value: 65, unit: '%', target: '目標 90%', trend: 'up', goodWhen: 'high' },
      { label: '問い合わせ平均対応', value: 6, unit: 'h', target: '目標 4h', trend: 'down', goodWhen: 'low' },
      { label: '手順 標準化率', value: 55, unit: '%', target: '目標 100%', trend: 'up', goodWhen: 'high' },
    ],
    milestones: [
      { label: '会員対応・テキスト発送のレビュー', done: true },
      { label: 'FAQ拡充', done: false },
      { label: '繁忙期工程の前倒し準備', done: false },
    ],
  },
  {
    id: 'guga',
    name: 'GUGA',
    color: '#7C3AED',
    pmId: 'taiki',
    progress: 50,
    health: 'good',
    nextAction: 'GUGAタスクの棚卸し・委員会／テキスト改訂の進捗整理',
    kgi: 'GUGA関連の進行（委員会運用・テキスト改訂）を可視化して回す',
    kgiProgress: 50,
    kpis: [
      { label: '進捗を整理できたテーマ', value: 2, unit: '件', target: '目標 4件', trend: 'flat', goodWhen: 'high' },
    ],
    milestones: [
      { label: 'GUGAラベルの新設', done: true },
      { label: 'GUGAタスクの棚卸し', done: false },
    ],
  },
];
export const PROJECT_MAP: Record<string, Project> = Object.fromEntries(PROJECTS.map((p) => [p.id, p]));

// ── MEMBERS — 権限の正本。exec と projects は独立（PRD §4-1）。 ──
export const MEMBERS: Member[] = [
  { id: 'taiki', name: '溝邊たいき', role: 'admin', exec: true, projects: PROJECTS.map((p) => p.id), initials: 'み' },
  { id: 'kurita', name: '栗田けいすけ', role: 'member', exec: false, projects: ['xfactory', 'ax'], initials: 'く' },
  { id: 'ren', name: '長谷川れん', role: 'member', exec: false, projects: ['sns', 'xfactory', 'yaoyorozu'], initials: 'は' },
  { id: 'doi', name: '土居周平', role: 'member', exec: true, projects: ['casa66', 'guga'], initials: 'ど' },
  { id: 'maria', name: 'マリア', role: 'member', exec: false, projects: ['casa66'], initials: 'マ' },
  { id: 'katayama', name: '片山', role: 'member', exec: false, projects: ['casa66'], initials: 'か' },
  { id: 'ishikawa', name: '石川', role: 'member', exec: false, projects: ['sns'], initials: 'い' },
  { id: 'hayato', name: 'はやと', role: 'member', exec: false, projects: ['jimukyoku'], initials: 'は' },
  { id: 'taichi', name: '松林太一', role: 'member', exec: true, projects: ['yaoyorozu'], initials: 'ま' },
  // 名簿のみ（機微でない組織構造）。Firestore に既に追加済みの2名を、名前/アバターを
  // 解決できるよう静的名簿にも足す。1on1 の機微データ本文は Firestore `oneonone` が
  // 正本で demoData には書かない。
  { id: 'keiichi', name: '啓一', role: 'member', exec: false, projects: ['casa66'], initials: '啓' },
  { id: 'ryu', name: 'リュウくん', role: 'member', exec: false, projects: ['jimukyoku'], initials: 'リ' },
];
export const MEMBER_MAP: Record<string, Member> = Object.fromEntries(MEMBERS.map((m) => [m.id, m]));

/** Viewer-switchable accounts (dev-only viewer-switch UI). */
export const VIEWERS = ['taiki', 'doi', 'taichi', 'kurita'];

// ── TOOLS_BY_PROJECT — PJダッシュのランチャー。url を設定すると「開く」リンクが有効になる ──
export interface ProjectTool {
  name: string;
  category: string;
  icon: string;
  note?: string;
  url?: string;
}
export const TOOLS_BY_PROJECT: Record<string, ProjectTool[]> = {
  xfactory: [
    { name: '制作アプリ（Web制作）', category: '業務アプリ', icon: 'app-window', note: '就労支援ワーカーが操作' },
    { name: 'MEOアプリ', category: '業務アプリ', icon: 'map-pin' },
    { name: '会員サイト', category: 'サイト', icon: 'globe', note: '情報収集→専用アプリ連携' },
    { name: 'Notion（案件・SFA）', category: 'ドキュメント', icon: 'database' },
    { name: 'Driveフォルダ', category: 'ドキュメント', icon: 'folder' },
    { name: 'Slack #xfactory', category: 'コミュニケーション', icon: 'message-circle' },
  ],
  casa66: [
    { name: '商品登録ツール', category: '業務アプリ', icon: 'app-window' },
    { name: 'LP・カタログ編集', category: '業務アプリ', icon: 'layout-template' },
    { name: 'Notion（商品DB）', category: 'ドキュメント', icon: 'database' },
    { name: 'Driveフォルダ', category: 'ドキュメント', icon: 'folder' },
    { name: 'Slack #casa66', category: 'コミュニケーション', icon: 'message-circle' },
  ],
  yakuin: [
    { name: '議事録', category: 'ドキュメント', icon: 'file-text' },
    { name: '経営ダッシュボード', category: '業務アプリ', icon: 'layout-dashboard' },
    { name: 'Driveフォルダ', category: 'ドキュメント', icon: 'folder' },
  ],
  yaoyorozu: [
    { name: 'イベント管理', category: '業務アプリ', icon: 'calendar-days' },
    { name: 'Notion', category: 'ドキュメント', icon: 'database' },
    { name: 'Driveフォルダ', category: 'ドキュメント', icon: 'folder' },
    { name: 'Slack', category: 'コミュニケーション', icon: 'message-circle' },
  ],
  sns: [
    { name: 'SNS投稿ストック（Notion）', category: 'ドキュメント', icon: 'database' },
    { name: '分析ダッシュ', category: '業務アプリ', icon: 'bar-chart-3' },
    { name: 'Driveフォルダ', category: 'ドキュメント', icon: 'folder' },
  ],
  ax: [
    { name: 'AXロードマップ', category: '業務アプリ', icon: 'route' },
    { name: 'Notion', category: 'ドキュメント', icon: 'database' },
    { name: 'Driveフォルダ', category: 'ドキュメント', icon: 'folder' },
  ],
  jimukyoku: [
    { name: '業務カレンダー', category: '業務アプリ', icon: 'calendar-days' },
    { name: 'FAQ', category: 'ドキュメント', icon: 'help-circle' },
    { name: 'Driveフォルダ', category: 'ドキュメント', icon: 'folder' },
  ],
};

// ── NOTES — 議事録。projectId が権限の正本。 ──
export const NOTES: MeetingNote[] = [
  {
    id: 'n1',
    title: 'xFactory 定例',
    projectId: 'xfactory',
    date: '2026-06-23',
    source: 'meet',
    meetLink: 'https://meet.google.com/xfa-defg-hij',
    participants: ['taiki', 'kurita', 'ren'],
    restricted: false,
    participantIds: ['taiki', 'kurita', 'ren'],
    confidence: '高',
    summary:
      'アウトバウンドの返信率レビューと制作アプリの改修方針を確認。就労支援ワーカーが操作するオペレーションの整理を進める。商品ごとの専用アプリ（Web制作）の改修を栗田が継続。会員サイトからの情報収集→専用アプリ連携の必須フローを再確認した。',
    topics: ['アウトバウンド返信率', '制作アプリ改修', '就労支援ワーカーのオペ', '会員サイト連携'],
    decisions: [
      '就労支援ワーカーのオペレーション手順を文書化する',
      '制作アプリ（Web制作）の改修を最優先で進める',
      '返信率0.3%を当面のGO判断ラインとして継続観測',
    ],
    actions: [
      { owner: 'kurita', task: '制作アプリ（Web制作）の改修', due: '2026-06-30', status: 'progress' },
      { owner: 'taiki', task: 'アウトバウンド返信率のレビューと共有', due: '2026-06-26', status: 'progress' },
      { owner: 'ren', task: 'アウトリーチリストの追加30件作成', due: '2026-06-27', status: 'todo' },
    ],
    transcript: [
      { t: '00:02', who: 'taiki', text: '今週の返信率から共有します。先週比で少し改善しています。' },
      { t: '00:48', who: 'kurita', text: '制作アプリ側は会員サイト連携の部分を作り直しています。来週には触れる形に。' },
      { t: '03:15', who: 'ren', text: 'リストは建設下請けを中心に追加で30件用意します。' },
    ],
  },
  {
    id: 'n2',
    title: 'casa.66 商品撮影MTG',
    projectId: 'casa66',
    date: '2026-06-20',
    source: 'notta',
    participants: ['doi', 'maria', 'katayama'],
    restricted: false,
    participantIds: ['doi', 'maria', 'katayama'],
    confidence: '高',
    summary:
      '新商品ラインの撮影スケジュールと、ECページ用カット数を確定。撮影は来週前半に実施し、編集は片山が担当。サムネイルのトーンを既存ブランドに揃える方針で合意した。',
    topics: ['撮影スケジュール', 'ECカット数', 'サムネイルトーン'],
    decisions: ['撮影は来週月・火で実施', '1商品あたり7カット（メイン1+サブ6）で統一'],
    actions: [
      { owner: 'maria', task: '撮影スケジュールの確定と関係者連絡', due: '2026-06-24', status: 'done' },
      { owner: 'katayama', task: '撮影後の画像編集・ECページ反映', due: '2026-06-27', status: 'progress' },
    ],
    transcript: [
      { t: '00:05', who: 'doi', text: '今回の新商品、カット数は前回と揃えましょう。' },
      { t: '01:20', who: 'maria', text: 'スケジュールは月火で押さえます。スタジオも確認済みです。' },
    ],
  },
  {
    id: 'n3',
    title: 'casa.66 在庫・出荷MTG',
    projectId: 'casa66',
    date: '2026-06-16',
    source: 'manual',
    confidence: '中',
    participants: ['doi', 'katayama'],
    restricted: false,
    participantIds: ['doi', 'katayama'],
    summary:
      '月初の在庫状況を確認し、欠品リスクのある2SKUの追加発注を決定。出荷オペレーションの遅延要因を洗い出し、梱包工程の見直しを次回までに検討する。',
    topics: ['在庫状況', '追加発注', '出荷オペ改善'],
    decisions: ['欠品リスクの2SKUを追加発注', '梱包工程の見直し案を次回までに用意'],
    actions: [
      { owner: 'doi', task: '追加発注の発注書作成・送付', due: '2026-06-19', status: 'done' },
      { owner: 'katayama', task: '梱包工程の見直し案ドラフト', due: '2026-06-23', status: 'progress' },
    ],
    transcript: [{ t: '00:03', who: 'doi', text: '在庫は2品番が来月で切れそうなので、今週中に発注します。' }],
  },
  {
    id: 'n4',
    title: '役員会議',
    projectId: 'yakuin',
    date: '2026-06-18',
    source: 'manual',
    confidence: '高',
    participants: ['taiki'],
    restricted: false,
    participantIds: ['taiki'],
    summary:
      '上期の事業進捗と下期の優先投資領域を確認。xFactoryのアウトバウンド実証を継続しつつ、AIアバター発信スタジオの内部展開を承認。コスト管理は各PJ単位でKPIを継続報告する方針。',
    topics: ['上期進捗', '下期投資', 'AX内部展開', 'KPI報告体制'],
    decisions: ['xFactoryアウトバウンド実証を下期も継続', 'AIアバター発信スタジオの内部展開を承認'],
    actions: [{ owner: 'taiki', task: '下期KPI報告フォーマットの整備', due: '2026-06-30', status: 'todo' }],
    transcript: [{ t: '00:10', who: 'taiki', text: '下期は投資領域を絞って、効果が見えているものに寄せます。' }],
  },
  {
    id: 'n5',
    title: 'ヤオヨロズ 月初定例',
    projectId: 'yaoyorozu',
    date: '2026-06-10',
    source: 'notta',
    confidence: '中',
    participants: ['taichi', 'ren'],
    restricted: false,
    participantIds: ['taichi', 'ren'],
    summary:
      '月初の数値を確認し、当月の重点施策を決定。前月のSNS反応が良かった企画を横展開する。コンテンツ制作の負荷分散を松林太一と長谷川で再分担した。',
    topics: ['月初数値', '重点施策', '制作分担'],
    decisions: ['前月好調だった企画を横展開', 'コンテンツ制作の分担を再設定'],
    actions: [
      { owner: 'taichi', task: '重点施策の制作スケジュール作成', due: '2026-06-14', status: 'done' },
      { owner: 'ren', task: '横展開企画のフォーマット整備', due: '2026-06-16', status: 'done' },
    ],
    transcript: [{ t: '00:08', who: 'taichi', text: '先月伸びた企画、今月は別チャネルでも回しましょう。' }],
  },
  {
    id: 'n6',
    title: 'SNS運用コンサル 週次',
    projectId: 'sns',
    date: '2026-06-19',
    source: 'meet',
    confidence: '高',
    meetLink: 'https://meet.google.com/sns-week-rev',
    participants: ['ren', 'ishikawa'],
    restricted: false,
    participantIds: ['ren', 'ishikawa'],
    summary:
      '週次のアカウント数値レビューと、来週投稿の方向性を確認。エンゲージメント率が改善したフォーマットを継続。クライアント報告資料のドラフトを石川が用意する。',
    topics: ['週次数値', '投稿方向性', 'クライアント報告'],
    decisions: ['高エンゲージメントのフォーマットを継続', 'クライアント報告資料を週次で定型化'],
    actions: [
      { owner: 'ishikawa', task: 'クライアント報告資料ドラフト作成', due: '2026-06-23', status: 'progress' },
      { owner: 'ren', task: '来週投稿コンテンツの方向性確定', due: '2026-06-21', status: 'done' },
    ],
    transcript: [{ t: '00:06', who: 'ren', text: 'このフォーマット、反応いいので来週も続けましょう。' }],
  },
  {
    id: 'n7',
    title: 'AXコンサル キックオフ',
    projectId: 'ax',
    date: '2026-06-17',
    source: 'notta',
    confidence: '中',
    participants: ['kurita', 'taiki'],
    restricted: false,
    participantIds: ['kurita', 'taiki'],
    summary:
      '新規AX支援案件のキックオフ。現状の業務フローをヒアリングし、生成AI導入の初期スコープを設定。まずは議事録・問い合わせ対応の自動化から着手する方針で合意した。',
    topics: ['現状ヒアリング', '導入スコープ', '初期着手領域'],
    decisions: ['議事録・問い合わせ対応の自動化から着手', '初期スコープを2業務に限定して効果検証'],
    actions: [
      { owner: 'kurita', task: '現状業務フローのヒアリング整理', due: '2026-06-24', status: 'progress' },
      { owner: 'taiki', task: '初期スコープ提案書のレビュー', due: '2026-06-25', status: 'todo' },
    ],
    transcript: [{ t: '00:12', who: 'kurita', text: 'まずは効果が見えやすい2業務に絞って入りましょう。' }],
  },
  {
    id: 'n8',
    title: '事務局 オペ定例',
    projectId: 'jimukyoku',
    date: '2026-06-12',
    source: 'meet',
    confidence: '高',
    meetLink: 'https://meet.google.com/jim-ope-mtg',
    participants: ['hayato'],
    restricted: false,
    participantIds: ['hayato'],
    summary:
      '月次の事務局オペレーションを確認。会員対応とテキスト発送の進捗をレビューし、問い合わせ対応のFAQ拡充を決定。繁忙期に向けた工程の前倒しを検討する。',
    topics: ['会員対応', 'テキスト発送', 'FAQ拡充'],
    decisions: ['問い合わせFAQを今月中に拡充', '繁忙期工程を前倒しで準備'],
    actions: [{ owner: 'hayato', task: 'FAQ項目の追加・整理', due: '2026-06-20', status: 'progress' }],
    transcript: [{ t: '00:04', who: 'hayato', text: '問い合わせが増えているので、FAQを先に厚くします。' }],
  },
];

// ── HISTORY_DONE — 過去完了タスクのデモ履歴（月次集計に厚みを出す）──
export interface HistoryDone {
  title: string;
  assignee: string;
  projectId: string;
  type: string;
  completedAt: string; // 'YYYY-MM-DD'
}
export const HISTORY_DONE: HistoryDone[] = [
  { title: 'アウトリーチ初期リスト50件の作成', assignee: 'ren', projectId: 'xfactory', type: 'create', completedAt: '2026-04-09' },
  { title: '会員サイトの初期コンテンツ整備', assignee: 'kurita', projectId: 'xfactory', type: 'create', completedAt: '2026-04-15' },
  { title: 'PoC（多言語マニュアル動画）の検証', assignee: 'taiki', projectId: 'xfactory', type: 'review', completedAt: '2026-04-22' },
  { title: '商品撮影ガイドラインの作成', assignee: 'maria', projectId: 'casa66', type: 'create', completedAt: '2026-04-11' },
  { title: '欠品リスクSKUの棚卸し', assignee: 'doi', projectId: 'casa66', type: 'review', completedAt: '2026-04-26' },
  { title: '高エンゲージ投稿フォーマットの特定', assignee: 'ishikawa', projectId: 'sns', type: 'review', completedAt: '2026-04-18' },
  { title: 'アウトリーチ返信テンプレ3案の作成', assignee: 'ren', projectId: 'xfactory', type: 'create', completedAt: '2026-05-07' },
  { title: '制作アプリの会員サイト連携 初期実装', assignee: 'kurita', projectId: 'xfactory', type: 'respond', completedAt: '2026-05-19' },
  { title: '上期KPI報告フォーマットの素案作成', assignee: 'taiki', projectId: 'yakuin', type: 'create', completedAt: '2026-05-13' },
  { title: 'ECページの商品説明リライト', assignee: 'katayama', projectId: 'casa66', type: 'create', completedAt: '2026-05-21' },
  { title: '週次報告テンプレの定型化（v1）', assignee: 'ishikawa', projectId: 'sns', type: 'create', completedAt: '2026-05-09' },
  { title: '横展開企画の初期セット制作', assignee: 'taichi', projectId: 'yaoyorozu', type: 'create', completedAt: '2026-05-27' },
  { title: 'FAQカテゴリ設計の整理', assignee: 'hayato', projectId: 'jimukyoku', type: 'review', completedAt: '2026-05-16' },
  { title: 'アウトリーチ返信率レビュー（5月分）', assignee: 'taiki', projectId: 'xfactory', type: 'review', completedAt: '2026-06-06' },
  { title: '撮影スケジュール（6月）の確定連絡', assignee: 'maria', projectId: 'casa66', type: 'contact', completedAt: '2026-06-04' },
  { title: '梱包工程フローの初稿作成', assignee: 'katayama', projectId: 'casa66', type: 'create', completedAt: '2026-06-09' },
  { title: 'クライアント月次レポート（5月）提出', assignee: 'ishikawa', projectId: 'sns', type: 'respond', completedAt: '2026-06-03' },
  { title: 'ヒアリング設計シートの作成', assignee: 'kurita', projectId: 'ax', type: 'create', completedAt: '2026-06-11' },
];

// ── KNOWLEDGE — scope が権限の正本（scope='all' 全社 / projectId PJ限定）──
export const KNOWLEDGE: Knowledge[] = [
  { id: 'k1', title: 'Claude Code のはじめ方', category: 'オンボーディング', scope: 'all', projectId: 'common', updated: '2026-06-22', preview: 'TEMPフォルダを開いて、やりたいことを日本語で話しかけるだけ。最初のセットアップ、よくある質問、困ったときの連絡先までを1ページにまとめています。専門用語はできるだけ使わずに説明しています。' },
  { id: 'k2', title: '議事録のアップロード手順', category: '手順書', scope: 'all', projectId: 'common', updated: '2026-06-21', preview: 'アップロード画面に音声・動画・テキストを入れると、AIが自動で文字起こし・要約・PJ判定まで行います。ファイル形式の対応一覧と、PJを手動指定したいときのやり方を解説しています。' },
  { id: 'k3', title: 'Driveフォルダ権限の申請方法', category: '手順書', scope: 'all', projectId: 'common', updated: '2026-06-16', preview: '共有ドライブで「閲覧」から「編集」に権限を上げたいときの申請の流れ。申請フォームの場所、承認の目安日数、よくある却下理由をまとめています。' },
  { id: 'k5', title: '請求書・経費の出し方', category: 'FAQ', scope: 'all', projectId: 'common', updated: '2026-06-18', preview: '請求書の発行タイミング、経費精算の締め日（25日）、領収書の提出先など、お金まわりのよくある質問をまとめたFAQです。改定があった場合はお知らせでも告知します。' },
  { id: 'k6', title: 'AI利用ガイドライン v2', category: '手順書', scope: 'all', projectId: 'common', updated: '2026-06-20', preview: '生成AIを業務で使うときの共通ルール。お客様の機密情報の取り扱い、出典の明示、成果物は人が最終チェックする——の3点が柱です。全メンバー必読の全社共通ガイドラインです。' },
  { id: 'k4', title: 'casa.66 商品登録マニュアル', category: '手順書', scope: 'casa66', projectId: 'casa66', updated: '2026-06-19', preview: 'ECサイトへの新商品登録の標準手順。カット数・サムネイルのトーン・説明文テンプレ・在庫連携の確認ポイントまで。casa.66 メンバー向けの限定マニュアルです。' },
  { id: 'k7', title: 'casa.66 撮影オペレーション手引き', category: '手順書', scope: 'casa66', projectId: 'casa66', updated: '2026-06-20', preview: '撮影スケジュールの押さえ方、1商品あたりのカット数（メイン1＋サブ6）、ライティングとトーンの基準、撮影後のEC反映までの流れ。撮影〜納品の属人化を減らすための手引きです。' },
  { id: 'k8', title: 'xFactory アウトリーチ運用手順', category: '手順書', scope: 'xfactory', projectId: 'xfactory', updated: '2026-06-22', preview: '送付先リストの作り方、初回文面のテンプレ、返信があったときの対応フロー、KPI（返信率0.3%）の見方まで。アウトバウンドの一連の流れをまとめた xFactory 限定の手順書です。' },
  { id: 'k9', title: 'xFactory 制作アプリ 操作ガイド（就労支援ワーカー向け）', category: 'オンボーディング', scope: 'xfactory', projectId: 'xfactory', updated: '2026-06-21', preview: '会員サイトで情報を集めてから専用アプリへ連携する流れを、はじめての方でも進められるように図解でまとめています。Web制作アプリの基本操作とつまずきやすいポイントを解説します。' },
  { id: 'k10', title: 'SNS週次レポートの作り方', category: '手順書', scope: 'sns', projectId: 'sns', updated: '2026-06-19', preview: '週次でクライアントに出す数値レポートの定型フォーマット。拾う指標、コメントの書き方、エンゲージメントが伸びたフォーマットの記録方法まで。SNS運用コンサル限定の手順です。' },
  { id: 'k11', title: 'SNS運用 投稿チェックFAQ', category: 'FAQ', scope: 'sns', projectId: 'sns', updated: '2026-06-17', preview: '投稿前に確認するポイント、煽り・誇大表現を避ける言い換え、ハッシュタグの付け方など、SNS投稿でよくある疑問をまとめたFAQです。' },
  { id: 'k12', title: 'AX導入ヒアリングシートの使い方', category: '手順書', scope: 'ax', projectId: 'ax', updated: '2026-06-18', preview: '新規AX支援案件で現状業務をヒアリングするときのシートの使い方。初期スコープを2業務に絞る考え方、効果検証の進め方まで。AXコンサル・IZANAI 限定の手順書です。' },
  { id: 'k13', title: 'ヤオヨロズ 月初定例の進め方', category: '手順書', scope: 'yaoyorozu', projectId: 'yaoyorozu', updated: '2026-06-10', preview: '月初の数値レビューから当月の重点施策決定、制作分担の再設定までの定例の進め方。好調企画を横展開するときのフォーマットも含みます。ヤオヨロズ限定の手順です。' },
  { id: 'k14', title: '事務局オペレーション・チェックリスト', category: '手順書', scope: 'jimukyoku', projectId: 'jimukyoku', updated: '2026-06-12', preview: '会員対応・テキスト発送・問い合わせ対応の月次チェックリスト。繁忙期に向けた工程の前倒し手順、FAQ更新の流れまで。事務局メンバー限定の手順書です。' },
  { id: 'k15', title: '役員会議 進行・KPI報告フォーマット', category: '手順書', scope: 'yakuin', projectId: 'yakuin', updated: '2026-06-18', preview: '役員会議のアジェンダの組み方と、下期に向けたKPI報告フォーマットのたたき台。各PJの数値の拾い方を含みます。役員会議の閲覧権限がある人だけに表示されます。' },
];

// ── CALENDAR_EVENTS — PJ無し＝全員/個人、PJ有り＝PJ権限者 ──
export const CALENDAR_EVENTS: CalendarEvent[] = [
  { id: 'c1', title: '7月の全社定例', date: '2026-07-03', time: '16:00', kind: 'meeting', meetLink: 'https://meet.google.com/temp-all-hands', note: '月初数値＋下期テーマの共有' },
  { id: 'c2', title: '経費精算の締め日', date: '2026-06-25', kind: 'plan', note: '領収書は事務局へ提出' },
  { id: 'c3', title: 'casa.66 新商品 撮影', date: '2026-06-23', time: '10:00', kind: 'plan', projectId: 'casa66', note: 'スタジオ予約済み' },
  { id: 'c4', title: 'casa.66 撮影（2日目）', date: '2026-06-24', time: '10:00', kind: 'plan', projectId: 'casa66' },
  { id: 'c5', title: 'xFactory アウトリーチ送付', date: '2026-06-26', time: '14:00', kind: 'plan', projectId: 'xfactory', note: '建設下請け 30件' },
  { id: 'c6', title: 'SNS クライアント定例', date: '2026-06-26', time: '11:00', kind: 'meeting', meetLink: 'https://meet.google.com/sns-client-mtg', projectId: 'sns' },
  { id: 'c7', title: 'xFactory 定例（来週）', date: '2026-06-25', time: '15:00', kind: 'meeting', meetLink: 'https://meet.google.com/xfa-weekly-25', projectId: 'xfactory', note: 'アウトバウンド返信率レビュー' },
  { id: 'c8', title: 'casa.66 撮影ふりかえり', date: '2026-06-26', time: '17:00', kind: 'meeting', meetLink: 'https://meet.google.com/casa-shoot-rv', projectId: 'casa66' },
];

// ── DRIVE_FILES — projectId 必須＝権限の正本 ──
export const DRIVE_FILES: DriveFile[] = [
  { id: 'd1', name: 'xFactory アウトリーチ進捗.xlsx', type: 'sheet', projectId: 'xfactory', noteId: 'n1', updated: '2026-06-23', owner: 'ren' },
  { id: 'd2', name: '制作アプリ 改修メモ.docx', type: 'doc', projectId: 'xfactory', noteId: 'n1', updated: '2026-06-22', owner: 'kurita' },
  { id: 'd3', name: 'xFactory 提案資料_v3.pptx', type: 'slide', projectId: 'xfactory', updated: '2026-06-20', owner: 'taiki' },
  { id: 'd4', name: '就労支援オペ手順（ドラフト）.docx', type: 'doc', projectId: 'xfactory', updated: '2026-06-19', owner: 'kurita' },
  { id: 'd5', name: 'xFactory 案件フォルダ', type: 'folder', projectId: 'xfactory', updated: '2026-06-23', owner: 'taiki' },
  { id: 'd6', name: 'casa.66 撮影カット一覧.xlsx', type: 'sheet', projectId: 'casa66', noteId: 'n2', updated: '2026-06-20', owner: 'maria' },
  { id: 'd7', name: '新商品 撮影香盤表.docx', type: 'doc', projectId: 'casa66', noteId: 'n2', updated: '2026-06-20', owner: 'maria' },
  { id: 'd8', name: 'casa.66 在庫管理表.xlsx', type: 'sheet', projectId: 'casa66', noteId: 'n3', updated: '2026-06-16', owner: 'doi' },
  { id: 'd9', name: '商品登録マニュアル.pdf', type: 'pdf', projectId: 'casa66', updated: '2026-06-19', owner: 'katayama' },
  { id: 'd10', name: 'casa.66 共有フォルダ', type: 'folder', projectId: 'casa66', updated: '2026-06-20', owner: 'doi' },
  { id: 'd11', name: '役員会議 議事メモ.docx', type: 'doc', projectId: 'yakuin', noteId: 'n4', updated: '2026-06-18', owner: 'taiki' },
  { id: 'd12', name: '下期KPI報告フォーマット.xlsx', type: 'sheet', projectId: 'yakuin', updated: '2026-06-18', owner: 'taiki' },
  { id: 'd13', name: 'ヤオヨロズ 月初数値.xlsx', type: 'sheet', projectId: 'yaoyorozu', noteId: 'n5', updated: '2026-06-10', owner: 'taichi' },
  { id: 'd14', name: '横展開企画フォーマット.docx', type: 'doc', projectId: 'yaoyorozu', updated: '2026-06-12', owner: 'ren' },
  { id: 'd15', name: 'SNS週次レポート_最新.pptx', type: 'slide', projectId: 'sns', noteId: 'n6', updated: '2026-06-19', owner: 'ishikawa' },
  { id: 'd16', name: 'SNS投稿カレンダー.xlsx', type: 'sheet', projectId: 'sns', updated: '2026-06-18', owner: 'ren' },
  { id: 'd17', name: 'AX導入ヒアリングシート.docx', type: 'doc', projectId: 'ax', noteId: 'n7', updated: '2026-06-17', owner: 'kurita' },
  { id: 'd18', name: '初期スコープ提案書.pptx', type: 'slide', projectId: 'ax', updated: '2026-06-18', owner: 'taiki' },
  { id: 'd19', name: '事務局オペ チェックリスト.xlsx', type: 'sheet', projectId: 'jimukyoku', noteId: 'n8', updated: '2026-06-12', owner: 'hayato' },
  { id: 'd20', name: 'FAQ原稿.docx', type: 'doc', projectId: 'jimukyoku', updated: '2026-06-20', owner: 'hayato' },
];

// ── AX_CASES — 本人名・金額・時給を持たない（横展開用）──
export const AX_CASES: AxCase[] = [
  { id: 'x1', title: '定例議事録の要約を自動化', category: '議事録', method: '会議の録音を本ハブに入れて、AIに要約・決定事項・アクションを自動抽出させた。手で書き起こす作業をなくした。', effect: '議事録準備 30分 → 5分' },
  { id: 'x2', title: '提案資料の初稿をAIで作成', category: '資料作成', method: '提案の骨子と参考デッキをAIに渡して、構成案とスライド初稿を生成。たたき台ができてから人が磨き込む流れに変えた。', effect: '初稿づくり 半日 → 1時間' },
  { id: 'x3', title: '問い合わせ返信テンプレの自動生成', category: '顧客対応', method: 'よくある問い合わせのパターンをAIに学習させ、状況に応じた返信ドラフトを自動生成。担当は確認して送るだけにした。', effect: '1件あたり 10分 → 2分' },
  { id: 'x4', title: 'SNS投稿の文案づくりをAIで量産', category: 'SNS運用', method: '好調だった投稿フォーマットをAIに渡して、トーンを保ったまま複数案を生成。人が選んで微調整する運用にした。', effect: '週次の文案づくりが約1/3に短縮' },
];

// ── PROMPTS — 全員閲覧 ──
export const PROMPTS: Prompt[] = [
  { id: 'p1', title: '議事録を要約するプロンプト', use: '議事録', tags: ['要約', '議事録'], body: '以下の会議の文字起こしを読んで、(1)3行サマリー (2)決定事項 (3)担当者つきアクションアイテム（期限の言及があれば含める）の3点を、箇条書きで日本語でまとめてください。事実だけを書き、推測は「※要確認」と明記してください。\n\n---\n（ここに文字起こしを貼り付け）' },
  { id: 'p2', title: 'アウトリーチ文面を作るプロンプト', use: 'アウトリーチ', tags: ['営業', 'xFactory'], body: '次の業種・地域の事業者向けに、ホームページ制作の初回アウトリーチ文面を120字程度で作ってください。売り込み感を抑え、「無料でモックを先に作る」提案を主軸にし、最後に短い問いかけで返信を促してください。\n\n業種：建設下請け\n地域：（地域名）\nトーン：丁寧・誠実' },
  { id: 'p3', title: '提案書の初稿を作るプロンプト', use: '資料作成', tags: ['提案', '初稿'], body: '以下の与件をもとに、提案書の構成案（見出し一覧）と、各セクションの初稿テキストを作ってください。読み手は非エンジニアの担当者です。専門用語は避け、「課題→打ち手→効果」の順で簡潔に。\n\n与件：（背景・課題・予算感・希望納期 などを貼り付け）' },
  { id: 'p4', title: 'SNS投稿案を量産するプロンプト', use: 'SNS運用', tags: ['SNS', '量産'], body: '次の「好調だった投稿」のトーンと型を保ったまま、同じテーマで別の切り口の投稿案を5本作ってください。各案は本文（全角140字以内）＋ハッシュタグ3つ。煽り表現・誇大表現は避けてください。\n\n好調投稿：（本文を貼り付け）' },
  { id: 'p5', title: '問い合わせ返信ドラフトを作るプロンプト', use: '顧客対応', tags: ['返信', 'テンプレ'], body: '以下のお問い合わせに対する返信ドラフトを作ってください。結論を冒頭に置き、相手の言葉で正面から答えること。丁寧だが冗長にならないトーンで、最後に次のアクションを一文添えてください。\n\nお問い合わせ：（本文を貼り付け）' },
  { id: 'p6', title: '長文を「やさしい日本語」に直すプロンプト', use: '社内', tags: ['推敲', '多言語'], body: '次の文章を、専門用語を避けた「やさしい日本語」に書き直してください。意味は変えず、一文を短く、主語を明確に。外国人スタッフにも伝わる平易さを目指してください。\n\n原文：（本文を貼り付け）' },
];

// ── MEMBER_APPS — 作品ギャラリー・全員閲覧 ──
export const MEMBER_APPS: MemberApp[] = [
  { id: 'ma1', name: '撮影カット管理ツール', authorId: 'maria', what: '撮影予定の商品とカット数を一覧管理し、撮り漏れをチェックできる小さなアプリ。', tag: 'casa.66' },
  { id: 'ma2', name: '在庫アラート表', authorId: 'katayama', what: '欠品リスクのあるSKUを色分けで表示し、追加発注の判断を助けるツール。', tag: 'casa.66' },
  { id: 'ma3', name: 'アウトリーチ進捗ボード', authorId: 'ren', what: '送付先リストの状態（未送/送信済/返信あり）をカンバンで管理するボード。', tag: 'xFactory' },
  { id: 'ma4', name: 'MEO作業チェックリスト', authorId: 'kurita', what: '店舗ごとのMEO作業項目をテンプレ化し、就労支援ワーカーが順番に進められるアプリ。', tag: 'xFactory' },
  { id: 'ma5', name: '投稿ネタ管理メモ', authorId: 'ishikawa', what: 'SNS投稿のアイデアを思いついた時にすぐ書き留め、状態で絞り込めるメモアプリ。', tag: 'SNS運用' },
  { id: 'ma6', name: 'FAQ検索ミニツール', authorId: 'hayato', what: '問い合わせFAQをキーワードで素早く引けるようにした事務局向けの検索ツール。', tag: '事務局' },
];

// ── 役員専用 ONE_ON_ONE — read は exec のみ。mentorNote は本人非共有。 ──
//   GUGA 1on1ダッシュ（dashboards/app/screens/team.jsx）相当までリッチ化。
//   スキルは6軸固定（自己理解 / 思考力 / 対人力 / 実行力 / 事業構想力 / 生成AI活用力）
//   で current/prev を持つ。phaseHistory / energyTrend / strengths / challenges /
//   currentWall / vision / milestones / lastMeetingNote / firstImpression を追加。
//   ダッシュの実メンバー（石川=ishikawa / 長谷川れん→該当者なし / 片山=katayama /
//   栗田=kurita）の成長・強み・Visionを該当する範囲で本アプリの架空メンバーへ翻訳。
export const ONE_ON_ONE: OneOnOne[] = [
  {
    id: 'doi', memberId: 'doi', phase: '安定', mbti: 'ESTJ',
    motivation: 4, satisfaction: 4, satTrend: [3, 3, 4, 4, 4, 4],
    skills: [
      { axis: '自己理解', current: 78, prev: 74 },
      { axis: '思考力', current: 80, prev: 76 },
      { axis: '対人力', current: 72, prev: 70 },
      { axis: '実行力', current: 88, prev: 85 },
      { axis: '事業構想力', current: 70, prev: 64 },
      { axis: '生成AI活用力', current: 58, prev: 48 },
    ],
    lastDate: '2026-06-18', nextDate: '2026-07-02',
    agenda: ['casa.66 撮影オペの属人化解消', '繁忙期に向けた工数の見通し', 'EC運用の自動化候補の洗い出し'],
    concern: '撮影〜EC反映の工程が自分に集中しがち。片山への移譲を進めたいが手が回らない。',
    dream: 'casa.66 をEC運用込みで仕組み化し、自分が抜けても回る体制をつくる。',
    mentorNote: 'PMとしての安定感は高い。一方で「抱え込み」傾向が満足度の天井になっている。次回は権限委譲を具体タスクで握る。評価面談では現等級維持＋オペ設計の実績を残す方向で握る想定。',
    phaseHistory: [
      { date: '2026-03', phase: '成長', wallFlag: false, note: 'casa.66 PMとして撮影〜EC運用を一手に。立ち上げを牽引' },
      { date: '2026-04', phase: '成長', wallFlag: true, note: '工程が自分に集中し抱え込み傾向が表面化。委譲が次の壁' },
      { date: '2026-05', phase: '安定', wallFlag: false, note: 'オペが安定稼働。属人化の解消と引き継ぎ設計に着手' },
      { date: '2026-06', phase: '安定', wallFlag: false, note: '繁忙期に向けた工数見通し・自動化候補の洗い出しへ' },
    ],
    energyTrend: [3, 4, 4, 4, 4, 3, 4, 4, 4],
    strengths: ['オペレーション設計力', 'PMとしての安定感・段取り力', '品質基準を言語化できる', '関係者調整が丁寧'],
    challenges: ['抱え込み傾向（権限委譲の苦手さ）', '自動化・AI活用の着手がこれから', '改善より実務優先になりがち'],
    currentWall: '抱え込みからの脱却＝撮影〜EC反映を片山に具体タスクで委譲し、自分が抜けても回る体制をつくる',
    vision: 'casa.66 をEC運用込みで仕組み化し、自分が抜けても回る体制をつくる。自分は改善・新商品企画側へ。',
    milestones: [
      { label: '撮影〜EC反映の標準手順を文書化', done: true },
      { label: '欠品リスクSKUの発注ルール化', done: true },
      { label: '片山への編集・EC反映の委譲（具体タスク3件）', done: false },
      { label: '梱包工程の見直し・リードタイム1.5日達成', done: false },
      { label: 'EC運用の自動化候補を1つ実装', done: false },
    ],
    lastMeetingNote: {
      date: '2026-06-18',
      topics: ['撮影〜EC反映の属人化', '繁忙期の工数見通し', '片山への委譲'],
      insights: [
        'オペは安定して回っているが、品質判断が土居一人に集中しているのが満足度の天井。',
        '「任せたいが手が回らない」状態。委譲そのものに時間を割く合意が必要。',
      ],
      todos: ['片山に渡す編集タスクを3件まで具体化する', '繁忙期の工数を週次で見える化する'],
    },
    firstImpression: 'PMとしての安定感と段取りが抜群。一方で「全部自分でやった方が早い」が口癖で、抱え込みが伸びしろの蓋になっている。',
  },
  {
    id: 'maria', memberId: 'maria', phase: '成長', mbti: 'ENFP',
    motivation: 5, satisfaction: 4, satTrend: [3, 4, 4, 5, 4, 5],
    skills: [
      { axis: '自己理解', current: 72, prev: 66 },
      { axis: '思考力', current: 70, prev: 62 },
      { axis: '対人力', current: 86, prev: 80 },
      { axis: '実行力', current: 78, prev: 72 },
      { axis: '事業構想力', current: 66, prev: 56 },
      { axis: '生成AI活用力', current: 64, prev: 52 },
    ],
    lastDate: '2026-06-19', nextDate: '2026-07-03',
    agenda: ['撮影ディレクションの幅出し', '新商品ラインの企画関与', 'AIでの文案・構成づくりの定着'],
    concern: '企画に関わりたい気持ちが強い一方、撮影実務の比重が高く時間が取りづらい。',
    dream: '撮影だけでなく商品企画から関わって、ブランドの世界観づくりを担いたい。',
    mentorNote: 'モチベーションが非常に高く伸びしろ大。期待を込めて企画関与の機会を渡したい。過負荷にならないよう実務の一部を巻き取る前提で。次フェーズでの抜擢候補。',
    phaseHistory: [
      { date: '2026-03', phase: '立ち上げ', wallFlag: false, note: '撮影アシスタントから参画。基礎を吸収' },
      { date: '2026-04', phase: '立ち上げ', wallFlag: true, note: '実務量が増え、企画に関わる時間が取れない葛藤' },
      { date: '2026-05', phase: '成長', wallFlag: false, note: '撮影ディレクションを一部任され手応えUP。AI文案づくりに着手' },
      { date: '2026-06', phase: '成長', wallFlag: false, note: '新商品ラインの企画関与の入口に。抜擢候補として育成中' },
    ],
    energyTrend: [3, 4, 4, 5, 4, 5, 4, 5, 5],
    strengths: ['対人力・巻き込み力が高い', 'ブランドの世界観を感覚で掴める', '高いモチベーションと吸収速度', 'AIでの文案・構成づくりを自分から試す'],
    challenges: ['実務過多で企画に充てる時間が取りづらい', '感覚を言語化・構造化する力はこれから', '優先順位づけが甘くなりがち'],
    currentWall: '撮影実務と企画関与の両立＝実務の一部を巻き取ってもらい、新商品ラインの企画に頭を使える時間をつくる',
    vision: '撮影だけでなく商品企画から関わって、casa.66 のブランドの世界観づくりを担う存在になる。',
    milestones: [
      { label: '撮影スケジュールの自走（関係者連絡まで）', done: true },
      { label: 'AIでの商品説明文ドラフトを定着', done: true },
      { label: '新商品ラインの企画ミーティングに正式参加', done: false },
      { label: '撮影ディレクションを1案件フルで担当', done: false },
      { label: 'ブランドトーンガイドの初稿を作成', done: false },
    ],
    lastMeetingNote: {
      date: '2026-06-19',
      topics: ['撮影ディレクションの幅出し', '企画関与の機会づくり', 'AI文案の定着'],
      insights: [
        'モチベーションが非常に高く、企画に関わりたい意欲が前面に。期待を込めて機会を渡したい。',
        '実務の比重が高く過負荷リスクあり。一部を巻き取る前提で企画時間を確保する。',
      ],
      todos: ['新商品ラインの企画MTGにマリアを入れる', '撮影実務の一部を片山と分担する'],
    },
    firstImpression: '明るく巻き込み力が高く、ブランドの空気を感覚で掴むのが得意。能力欲が強く、機会を渡すほど伸びるタイプ。',
  },
  {
    id: 'katayama', memberId: 'katayama', phase: '停滞ぎみ', mbti: 'ISTP',
    motivation: 3, satisfaction: 3, satTrend: [4, 4, 3, 3, 3, 3],
    skills: [
      { axis: '自己理解', current: 64, prev: 64 },
      { axis: '思考力', current: 66, prev: 62 },
      { axis: '対人力', current: 52, prev: 52 },
      { axis: '実行力', current: 70, prev: 70 },
      { axis: '事業構想力', current: 58, prev: 50 },
      { axis: '生成AI活用力', current: 72, prev: 58 },
    ],
    lastDate: '2026-06-16', nextDate: '2026-06-30',
    agenda: ['編集作業の負荷感の確認', '裁量を持てる領域づくり', 'AIでの編集効率化の試行'],
    concern: '編集の指示待ちが続いて、自分で動かせる範囲が狭いと感じている。手応えが薄い。',
    dream: '編集だけでなく、撮影〜納品の品質基準づくりを任されたい。',
    mentorNote: '満足度が緩やかに低下。停滞シグナルあり。技術は確かなので「任せる領域」を明確に渡すのが効きそう。次回1on1の最優先。放置すると離脱リスク中。',
    phaseHistory: [
      { date: '2026-03', phase: '成長', wallFlag: false, note: '編集スキルが安定。LP・カタログ編集を担当' },
      { date: '2026-04', phase: '安定', wallFlag: false, note: '編集オペは確実。AIで情報収集・LP制作の自動化に着手' },
      { date: '2026-05', phase: '停滞ぎみ', wallFlag: true, note: '指示待ちが続き裁量の狭さに手応え低下。満足度が緩やかに低下' },
      { date: '2026-06', phase: '停滞ぎみ', wallFlag: true, note: '「任せる領域」を渡せていない。次回1on1の最優先・離脱リスク中' },
    ],
    energyTrend: [4, 4, 4, 4, 3, 3, 4, 3, 3],
    strengths: ['編集・制作の技術力が確か', 'AI実装に自分から手を動かせる（自動化・LP制作）', '黙々と仕上げる集中力', '生成AI活用力が強み軸になりつつある'],
    challenges: ['指示待ちで裁量の狭さに手応えが薄い', '自分から領域を取りにいく主体性', '対人・発信が控えめで意図が伝わりにくい'],
    currentWall: '「任せる領域」が渡されず手応えが薄い停滞＝撮影〜納品の品質基準づくりなど裁量のある領域を明確に渡す',
    vision: '編集だけでなく、撮影〜納品の品質基準づくりを任され、AIで制作オペを効率化する側に回る。',
    milestones: [
      { label: 'ECページの商品説明リライトを完了', done: true },
      { label: 'AIで情報収集・LP制作の自動化に着手', done: true },
      { label: '品質基準（カット数・トーン）づくりを担当', done: false },
      { label: '梱包工程フローの見直し案を主導', done: false },
      { label: '編集効率化アプリを業務に定着', done: false },
    ],
    lastMeetingNote: {
      date: '2026-06-16',
      topics: ['編集作業の負荷感', '裁量を持てる領域づくり', 'AIでの編集効率化'],
      insights: [
        '満足度が緩やかに低下し停滞シグナル。技術は確かなので「任せる領域」を渡すのが効きそう。',
        '生成AI活用は本人の強みになりつつある。自動化テーマで裁量を渡すと火がつく可能性。',
      ],
      todos: ['撮影〜納品の品質基準づくりを片山に任せる', 'AI編集効率化のテーマを1つ握る'],
    },
    firstImpression: '寡黙で技術は確か。指示通りは正確だが、裁量がないと手応えを失うタイプ。任せる領域を明示できるかが鍵。',
  },
  {
    id: 'ishikawa', memberId: 'ishikawa', phase: '成長', mbti: 'INFJ',
    motivation: 4, satisfaction: 4, satTrend: [3, 3, 4, 4, 4, 4],
    skills: [
      { axis: '自己理解', current: 74, prev: 68 },
      { axis: '思考力', current: 76, prev: 68 },
      { axis: '対人力', current: 66, prev: 62 },
      { axis: '実行力', current: 72, prev: 64 },
      { axis: '事業構想力', current: 70, prev: 58 },
      { axis: '生成AI活用力', current: 68, prev: 56 },
    ],
    lastDate: '2026-06-19', nextDate: '2026-07-04',
    agenda: ['クライアント報告の定型化', '分析コメントの質を上げる', '提案への一歩踏み込み'],
    concern: '数値はまとめられるが、示唆出し・提案に踏み込む自信がまだ持てない。',
    dream: 'SNS運用で「数字を語れる人」になって、クライアントに提案できるようになりたい。',
    mentorNote: '丁寧で信頼できる。あと一歩の「踏み込み」を後押しする段階。報告フォーマット定型化で時間を空け、示唆出しに頭を使える状態をつくる。',
    phaseHistory: [
      { date: '2026-03', phase: '立ち上げ', wallFlag: false, note: 'SNS運用に参画。数値レポートの基礎を習得' },
      { date: '2026-04', phase: '成長', wallFlag: false, note: '高エンゲージ投稿フォーマットを特定。分析の型が定着' },
      { date: '2026-05', phase: '成長', wallFlag: true, note: '数値はまとめられるが示唆出し・提案への踏み込みが次の壁' },
      { date: '2026-06', phase: '成長', wallFlag: true, note: '報告定型化で時間を空け、示唆出しに頭を使う段階へ' },
    ],
    energyTrend: [3, 3, 4, 4, 4, 3, 4, 4, 4],
    strengths: ['数値を正確にまとめる力', '丁寧で信頼できる仕事ぶり', '構造化・分析の適性', '相手の意図を汲む対人感度'],
    challenges: ['示唆出し・提案に踏み込む自信のなさ', '「なぜその数字か」を語る力がこれから', '自分の仮説を断言できない'],
    currentWall: '示唆出し・提案への踏み込み＝数値の集計から一歩進んで「だから何をすべきか」をクライアントに提案できる状態',
    vision: 'SNS運用で「数字を語れる人」になり、クライアントに自分の仮説で提案できるようになる。',
    milestones: [
      { label: '高エンゲージ投稿フォーマットの特定', done: true },
      { label: '週次報告テンプレの定型化（v1）', done: true },
      { label: '分析コメントに「示唆」を一文添える運用', done: false },
      { label: 'クライアント提案を1件自分で起案', done: false },
      { label: '報告書 定型化率100%を達成', done: false },
    ],
    lastMeetingNote: {
      date: '2026-06-19',
      topics: ['クライアント報告の定型化', '分析コメントの質', '提案への踏み込み'],
      insights: [
        '丁寧で信頼できるが、あと一歩の「踏み込み」が課題。後押しする段階。',
        '報告定型化で時間を空ければ、示唆出しに頭を使える。仕組みで踏み込みを支援する。',
      ],
      todos: ['報告テンプレを完成させ作業時間を削減', '分析コメントに必ず示唆を一文入れる'],
    },
    firstImpression: '丁寧で誠実、数値の扱いが堅実。一方で自分の仮説を断言するのが苦手で、踏み込みの一歩を後押しすると伸びる。',
  },
  {
    id: 'hayato', memberId: 'hayato', phase: '安定', mbti: 'ISFJ',
    motivation: 4, satisfaction: 5, satTrend: [4, 4, 5, 5, 5, 5],
    skills: [
      { axis: '自己理解', current: 72, prev: 68 },
      { axis: '思考力', current: 66, prev: 62 },
      { axis: '対人力', current: 70, prev: 66 },
      { axis: '実行力', current: 84, prev: 80 },
      { axis: '事業構想力', current: 60, prev: 52 },
      { axis: '生成AI活用力', current: 62, prev: 50 },
    ],
    lastDate: '2026-06-12', nextDate: '2026-06-26',
    agenda: ['事務局オペの標準化', 'リュウくんへの引き継ぎ設計', '繁忙期の工程前倒し'],
    concern: '安定して回せているが、自分しか分かっていない手順が残っている。',
    dream: '事務局を「誰でも回せる」状態にして、自分は改善側に回りたい。',
    mentorNote: '満足度・安定感ともに高い。オペレーションの要。標準化と引き継ぎを評価対象に据え、組織化の実績として残す方向で握る。離脱リスク低。',
    phaseHistory: [
      { date: '2026-03', phase: '成長', wallFlag: false, note: '事務局オペを一手に。会員対応・発送を安定運用' },
      { date: '2026-04', phase: '安定', wallFlag: false, note: 'FAQカテゴリ設計を整理。標準化に着手' },
      { date: '2026-05', phase: '安定', wallFlag: false, note: '属人手順が残るのが課題。引き継ぎ設計が次のテーマ' },
      { date: '2026-06', phase: '安定', wallFlag: false, note: '標準化・引き継ぎを評価対象に。組織化の実績づくりへ' },
    ],
    energyTrend: [4, 4, 5, 5, 5, 4, 5, 5, 5],
    strengths: ['オペレーションを止めない安定感', '会員対応・発送の確実な実行力', '誰にでも丁寧な対応', '標準化・仕組み化への志向'],
    challenges: ['自分しか分からない手順が残っている', '改善側に回る時間が取れていない', 'AI活用・自動化の余地が大きい'],
    currentWall: '属人手順の解消＝自分しか分かっていない手順を標準化・引き継ぎし、自分は改善側に回る',
    vision: '事務局を「誰でも回せる」状態にして、自分は改善・効率化を主導する側に回る。',
    milestones: [
      { label: '事務局オペ チェックリストの整備', done: true },
      { label: 'FAQカテゴリ設計の整理', done: true },
      { label: '問い合わせFAQをカバー率90%まで拡充', done: false },
      { label: 'リュウくんへの引き継ぎ手順を文書化', done: false },
      { label: '繁忙期工程の前倒し準備を完了', done: false },
    ],
    lastMeetingNote: {
      date: '2026-06-12',
      topics: ['事務局オペの標準化', 'リュウくんへの引き継ぎ', '繁忙期の工程前倒し'],
      insights: [
        '満足度・安定感ともに高くオペの要。離脱リスクは低い。',
        '属人手順の標準化と引き継ぎを評価対象に据え、組織化の実績として残す。',
      ],
      todos: ['属人手順を洗い出して文書化する', 'FAQ拡充を今月中に完了する'],
    },
    firstImpression: '誠実で粘り強く、オペレーションを止めない安心感がある。標準化・引き継ぎを実績として残せば組織化の中核に。',
  },
];

// ── 役員専用 AX_* — 給与/時給フィールドを持たない。経済価値＝総削減時間×4,000円。 ──
export const AX_MEMBER_PROGRESS: AxMemberProgress[] = [
  { memberId: 'doi', savedHours: 18, proposals: 4, fillRate: 90 },
  { memberId: 'maria', savedHours: 22, proposals: 6, fillRate: 100 },
  { memberId: 'katayama', savedHours: 9, proposals: 2, fillRate: 55 },
  { memberId: 'ishikawa', savedHours: 16, proposals: 5, fillRate: 85 },
  { memberId: 'hayato', savedHours: 14, proposals: 3, fillRate: 80 },
  { memberId: 'ren', savedHours: 26, proposals: 7, fillRate: 95 },
  { memberId: 'kurita', savedHours: 31, proposals: 8, fillRate: 100 },
  { memberId: 'taichi', savedHours: 11, proposals: 3, fillRate: 60 },
];

export const AX_KPI: AxKpi = {
  reduceHours: AX_MEMBER_PROGRESS.reduce((s, m) => s + m.savedHours, 0),
  createHours: 38,
  proposals: AX_MEMBER_PROGRESS.reduce((s, m) => s + m.proposals, 0),
};

export const AX_PHASES: AxPhase[] = [
  { phase: 'Phase 1', title: '使ってみる', desc: '全員が自分の業務でAIを1つ試す。議事録・文案・調べ物から。', status: 'done' },
  { phase: 'Phase 2', title: '型をつくる', desc: '効いた使い方を手順化し、チームで横展開する。', status: 'active' },
  { phase: 'Phase 3', title: '仕組みに乗せる', desc: '専用アプリ・自動化に落とし込み、人がいなくても回る形に。', status: 'todo' },
];

export const AX_SUPPORT: AxSupport[] = [
  { memberId: 'taiki', role: 'CxO / 全体設計', area: 'AX推進の全体方針・評価設計・経営報告' },
  { memberId: 'kurita', role: '技術ケツ持ち', area: '専用アプリ開発・難所の技術支援（IZANAI）' },
  { memberId: 'ren', role: '横展開リード', area: 'SNS・コンテンツ領域の型づくりと普及' },
];

export const AX_WINS: AxWin[] = [
  { memberId: 'kurita', title: '制作アプリの定型処理をAIで自動生成', effect: '1案件あたり 90分 → 25分' },
  { memberId: 'ren', title: 'SNS文案の量産フローをチームに展開', effect: '週次の文案づくりが約1/3に短縮' },
  { memberId: 'maria', title: '商品説明文の初稿をAIでドラフト', effect: '1商品 30分 → 8分' },
  { memberId: 'hayato', title: '問い合わせ返信テンプレの自動生成', effect: '1件あたり 10分 → 2分' },
];
